import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { UserServices } from "../../../services/user/user.services";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import { UserRolesService } from "../../../services/user/userroles.services";
import Select from 'react-select';


const AddUserComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userId = location.state?.userId || 0;
    const isEditMode = !!location.state?.userId;
    const [usersRoles, setUsersRoles] = useState([]);
    const [users, setUsers] = useState([]);

    const formik = useFormik({
        initialValues: {
            roleId: 0,
            username: "",
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
            maxDiscount: "",
            myTeam: [],
            approvalForQuote: false,
            approvalForInvoice: false,
            userPassword: "",
            confirmPassword: "",
            isActive: true,
        },
        validationSchema: Yup.object({
            username: Yup.string().required("Username required"),
            email: Yup.string().required("Email required"),
            maxDiscount: Yup.string().required("MaxDiscount required"),
            // userPassword: Yup.string().required("Password is required"),
            // confirmPassword: Yup.string().required("Confirm Your Password"),
            userPassword: isEditMode
                ? Yup.string().notRequired()
                : Yup.string().required("Password is required").min(6, "Password must be at least 6 characters"),

            confirmPassword: isEditMode
                ? Yup.string().notRequired()
                : Yup.string().required("Confirm Your Password").oneOf([Yup.ref("userPassword"), null], "Passwords must match"),
        }),
        onSubmit: async (values) => {
            try {
                const data = { ...values, roleId: Number(values.roleId) };

                if (isEditMode) {
                    if (!values.userPassword) {
                        delete data.userPassword;
                    }
                    await UserServices.updateUserById(userId, data);
                    navigate('/users');
                } else {
                    if (values.userPassword !== values.confirmPassword) {
                        handleNotify("Passwords must match", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                        return;
                    }
                    await UserServices.addNewUser(data);
                    navigate('/users');
                }
            } catch (error) {
                handleNotify('An error occurred while saving Users.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        }
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchUserDetail = async () => {
                try {
                    const data = await UserServices.getUserById(userId);
                    if (data) {
                        formik.setValues({
                            roleId: data?.roleId,
                            username: data?.username,
                            firstName: data?.firstName,
                            lastName: data?.lastName,
                            phone: data?.phone,
                            email: data?.email,
                            maxDiscount: data?.maxDiscount,
                            myTeam: Array.isArray(data?.myTeam) ? data.myTeam : [],
                            approvalForQuote: data?.approvalForQuote,
                            approvalForInvoice: data?.approvalForInvoice,
                            userPassword: data?.userPassword,
                            confirmPassword: data?.confirmPassword,
                            isActive: data?.isActive === true ? true : false,
                        });
                    }
                }
                catch (error) {
                    console.log('Error fetching user by id', error)
                }
            }
            fetchUserDetail();
        }
    }, [])
    const fetchUsersRole = async () => {
        try {
            const response = await UserRolesService.getAllUserRoles();
            const sortedUsersRole = response.sort((a, b) => a.roleId - b.roleId);
            const userRoleOptions = sortedUsersRole.map(usersrole => ({
                value: usersrole.roleId,
                label: usersrole.roleName
            }));
            if (!formik.values.roleId && userRoleOptions.length > 0) {
                formik.setFieldValue('roleId', userRoleOptions[0].value)
            }
            setUsersRoles(userRoleOptions);
        } catch (error) {
            console.error("Error fetching Users role:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await UserServices.getAllUsers();
            const sortedUsers = response.sort((a, b) => a.userId - b.userId);
            const usersOptions = sortedUsers.map(user => ({
                value: user.userId,
                label: user.username
            }));
            if (!formik.values.userId && usersOptions.length > 0) {
                formik.setFieldValue('roleId', usersOptions[0].value)
            }
            setUsers(usersOptions);
        } catch (error) {
            console.error("Error fetching Users role:", error);
        }
    };

    useEffect(() => {
        fetchUsersRole();
        fetchUsers();
    }, []);

    const handleBackClick = () => {
        navigate('/users');
    };

    return (
        <div className="page-wrapper p-4 m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div
                        className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                        onClick={handleBackClick}
                    >
                        Users
                    </div>
                    <div className="ps-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item active" aria-current="page">
                                    {isEditMode ? 'Update User' : 'Add User'}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="ms-auto">
                        <button className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm" onClick={handleBackClick}>Back</button>
                    </div>
                </div>

                <div className="card p-4 radius-10 border-top border-0 border-4 border-primary">
                    <div className="card-body">
                        <form onSubmit={formik.handleSubmit}>
                            <div className="row">
                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="roleId" className="form-label d-flex">
                                        Role :
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="roleId"
                                            name="roleId"
                                            options={usersRoles}
                                            value={usersRoles.find(option => option.value === formik.values.roleId)}
                                            onChange={(selectedOption) => formik.setFieldValue('roleId', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="username" className="form-label d-flex">
                                        Username  <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="username"
                                        className={`form-control ${formik.touched.username && formik.errors.username ? 'is-invalid' : ''}`}
                                        placeholder="Username"
                                        value={formik.values.username}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.username && formik.errors.username && (
                                        <div className="invalid-feedback d-flex">{formik.errors.username}</div>
                                    )}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="firstName" className="form-label d-flex">
                                        FirstName :
                                    </label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        className="form-control"
                                        placeholder="FirstName"
                                        value={formik.values.firstName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>

                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="lastName" className="form-label d-flex">
                                        LastName :
                                    </label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        className="form-control"
                                        placeholder="LastName"
                                        value={formik.values.lastName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="phone" className="form-label d-flex">
                                        Phone :
                                    </label>
                                    <input
                                        type="text"
                                        id="phone"
                                        className="form-control"
                                        placeholder="Phone"
                                        value={formik.values.phone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>

                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="email" className="form-label d-flex">
                                        Email <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="email"
                                        className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                                        placeholder="Email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.email && formik.errors.email && (
                                        <div className="invalid-feedback d-flex">{formik.errors.email}</div>
                                    )}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="maxDiscount" className="form-label d-flex">
                                        Max Discount <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="maxDiscount"
                                        className={`form-control ${formik.touched.maxDiscount && formik.errors.maxDiscount ? 'is-invalid' : ''}`}
                                        placeholder="0.00"
                                        value={formik.values.maxDiscount}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.maxDiscount && formik.errors.maxDiscount && (
                                        <div className="invalid-feedback d-flex">{formik.errors.maxDiscount}</div>
                                    )}
                                </div>

                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="myTeam" className="form-label d-flex">
                                        My Team :
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="myTeam"
                                            name="myTeam"
                                            options={users}
                                            value={users.filter(user => formik.values.myTeam.includes(user.value))}
                                            onChange={(selectedOptions) => {
                                                const selectedIds = selectedOptions.map(option => option.value);
                                                formik.setFieldValue('myTeam', selectedIds);
                                            }}
                                            placeholder="Select users"
                                            isMulti
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-2">
                                {/* Info Alert Message */}
                                <div className="col-sm-3"></div>
                                <div className="col-sm-12">
                                    <div className="alert alert-info bg-info text-white border-0 d-flex justify-content-between align-items-center py-2 px-3 fade show">
                                        <div>
                                            Leave the password field empty if you don't want to change.
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white"
                                            data-bs-dismiss="alert"
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                </div>
                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="userPassword" className="form-label d-flex">
                                        Password <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="password"
                                        id="userPassword"
                                        className={`form-control ${formik.touched.userPassword && formik.errors.userPassword ? 'is-invalid' : ''}`}
                                        placeholder="Password"
                                        value={formik.values.userPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.userPassword && formik.errors.userPassword && (
                                        <div className="invalid-feedback d-flex">{formik.errors.userPassword}</div>
                                    )}
                                </div>
                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                    <label htmlFor="confirmPassword" className="form-label d-flex">
                                        ConfirmPassword <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        className={`form-control ${formik.touched.confirmPassword && formik.errors.confirmPassword ? 'is-invalid' : ''}`}
                                        placeholder="Confirm Password"
                                        value={formik.values.confirmPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                                        <div className="invalid-feedback d-flex">{formik.errors.confirmPassword}</div>
                                    )}
                                </div>

                                <div className="d-flex mt-3">
                                    <div className="col-12 col-lg-6 col-md-4 mb-3 d-flex">
                                        <label htmlFor="approvalRequirementFor" className="form-label d-flex">Approval Required For :</label>
                                        <div className="form-check form-check-inline">
                                            <input
                                                type="checkbox"
                                                id="approvalForQuote"
                                                name="approvalForQuote"
                                                className="form-check-input"
                                                value={true}
                                                checked={formik.values.approvalForQuote}
                                                onChange={formik.handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="quote">
                                                Quote
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline ">
                                            <input
                                                type="checkbox"
                                                id="approvalForInvoice"
                                                name="approvalForInvoice"
                                                className="form-check-input"
                                                value={false}
                                                onChange={formik.handleChange}
                                                checked={formik.values.approvalForInvoice}
                                            />
                                            <label className="form-check-label" htmlFor="invoice">
                                                Invoice
                                            </label>
                                        </div>
                                    </div>

                                    <div className="col-12 col-lg-6 col-md-4 d-flex">
                                        <label className="form-label me-3">Status:</label>
                                        <div className="form-check form-check-inline me-3">
                                            <input
                                                type="radio"
                                                id="active"
                                                name="isActive"
                                                className="form-check-input"
                                                value={true}
                                                checked={formik.values.isActive === true}
                                                onChange={() => formik.setFieldValue("isActive", true)}
                                            />
                                            <label className="form-check-label" htmlFor="active">
                                                Active
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input
                                                type="radio"
                                                id="inactive"
                                                name="isActive"
                                                className="form-check-input"
                                                value={false}
                                                checked={formik.values.isActive === false}
                                                onChange={() => formik.setFieldValue("isActive", false)}
                                            />
                                            <label className="form-check-label" htmlFor="inactive">
                                                Inactive
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="d-flex mt-4 justify-content-end">
                                <div className="btn-group">
                                    <button type="button" className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm" onClick={handleBackClick}>
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    >
                                        {isEditMode ? "Update" : "Save"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddUserComponent;
